import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, ChevronDown } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const AnimalList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("Inbred");
  
  const strainsByCategory = {
    "Inbred": [
      {
        id: "002448",
        name: "129S1/SvImJ",
        description: "129 substrain commonly used for gene targeting",
        jaxUrl: "https://www.jax.org/strain/002448"
      },
      {
        id: "000691",
        name: "129X1/SvJ",
        description: "129 substrain for embryonic stem cell work",
        jaxUrl: "https://www.jax.org/strain/000691"
      },
      {
        id: "000646",
        name: "A/J",
        description: "Susceptible to lung tumors and chemical carcinogenesis",
        jaxUrl: "https://www.jax.org/strain/000646"
      },
      {
        id: "000648",
        name: "AKR/J",
        description: "High incidence of spontaneous leukemia",
        jaxUrl: "https://www.jax.org/strain/000648"
      },
      {
        id: "001026",
        name: "BALB/cByJ",
        description: "BALB/c substrain from Bar Harbor",
        jaxUrl: "https://www.jax.org/strain/001026"
      },
      {
        id: "000651",
        name: "BALB/cJ",
        description: "White strain, immunology and oncology research",
        jaxUrl: "https://www.jax.org/strain/000651"
      },
      {
        id: "002282",
        name: "BTBR T+ Itpr3 tf/J",
        description: "Genetic background for autism studies",
        jaxUrl: "https://www.jax.org/strain/002282"
      },
      {
        id: "000659",
        name: "C3H/HeJ",
        description: "Complement deficient strain",
        jaxUrl: "https://www.jax.org/strain/000659"
      },
      {
        id: "000664",
        name: "C57BL/6J",
        description: "Most widely used inbred strain worldwide",
        jaxUrl: "https://www.jax.org/strain/000664"
      },
      {
        id: "005304",
        name: "C57BL/6NJ",
        description: "NIH substrain of C57BL/6",
        jaxUrl: "https://www.jax.org/strain/005304"
      },
      {
        id: "000665",
        name: "C57BL/10J",
        description: "C57BL background strain for congenic studies",
        jaxUrl: "https://www.jax.org/strain/000665"
      },
      {
        id: "000656",
        name: "CBA/J",
        description: "Good breeding performance, hearing research",
        jaxUrl: "https://www.jax.org/strain/000656"
      },
      {
        id: "000654",
        name: "CBA/CaJ",
        description: "CBA substrain from California",
        jaxUrl: "https://www.jax.org/strain/000654"
      },
      {
        id: "000670",
        name: "DBA/1J",
        description: "Collagen-induced arthritis model",
        jaxUrl: "https://www.jax.org/strain/000670"
      },
      {
        id: "000671",
        name: "DBA/2J",
        description: "Audiogenic seizure susceptibility",
        jaxUrl: "https://www.jax.org/strain/000671"
      },
      {
        id: "001800",
        name: "FVB/NJ",
        description: "Excellent breeding, transgenic studies",
        jaxUrl: "https://www.jax.org/strain/001800"
      },
      {
        id: "001976",
        name: "NOD/ShiLtJ",
        description: "Non-obese diabetic model",
        jaxUrl: "https://www.jax.org/strain/001976"
      },
      {
        id: "000686",
        name: "SJL/J",
        description: "Experimental autoimmune encephalomyelitis",
        jaxUrl: "https://www.jax.org/strain/000686"
      },
      {
        id: "005314",
        name: "TALLYHO/JngJ",
        description: "Polygenic obesity and type 2 diabetes",
        jaxUrl: "https://www.jax.org/strain/005314"
      }
    ],
    "Outbred": [
      {
        id: "009376",
        name: "Diversity Outbred J:DO",
        description: "Genetically diverse outbred population",
        jaxUrl: "https://www.jax.org/strain/009376"
      }
    ],
    "Hybrid": [
      {
        id: "101045",
        name: "B6129SF2/J",
        description: "B6 x 129 F2 hybrid for mapping studies",
        jaxUrl: "https://www.jax.org/strain/101045"
      },
      {
        id: "100010",
        name: "B6C3F1/J",
        description: "C57BL/6J x C3H/HeJ F1 hybrid",
        jaxUrl: "https://www.jax.org/strain/100010"
      },
      {
        id: "100011",
        name: "B6CBAF1/J",
        description: "C57BL/6J x CBA/J F1 hybrid",
        jaxUrl: "https://www.jax.org/strain/100011"
      },
      {
        id: "100006",
        name: "B6D2F1/J",
        description: "C57BL/6J x DBA/2J F1 hybrid",
        jaxUrl: "https://www.jax.org/strain/100006"
      },
      {
        id: "100012",
        name: "B6SJLF1/J",
        description: "C57BL/6J x SJL/J F1 hybrid",
        jaxUrl: "https://www.jax.org/strain/100012"
      },
      {
        id: "100007",
        name: "CB6F1/J",
        description: "CBA/J x C57BL/6J F1 hybrid",
        jaxUrl: "https://www.jax.org/strain/100007"
      },
      {
        id: "100008",
        name: "NZBWF1/J",
        description: "NZB/BlNJ x NZW/LacJ F1 lupus model",
        jaxUrl: "https://www.jax.org/strain/100008"
      }
    ],
    "Immunodeficient": [
      {
        id: "005557",
        name: "NOD.Cg-Prkdc scid Il2rg tm1Wjl/SzJ",
        description: "NSG™ - Highly immunodeficient for xenograft studies",
        jaxUrl: "https://www.jax.org/strain/005557"
      },
      {
        id: "007799",
        name: "NOD.Cg-Rag1 tm1Mom Il2rg tm1wjl/SzJ",
        description: "NRG - Immunodeficient strain for transplantation",
        jaxUrl: "https://www.jax.org/strain/007799"
      },
      {
        id: "013062",
        name: "NOD.Cg-Prkdc scid Il2rg tm1wjl Tg(CMV-IL3,CSF2,KITLG)1Eav/MloySzJ",
        description: "NSGS - Enhanced for human hematopoiesis",
        jaxUrl: "https://www.jax.org/strain/013062"
      },
      {
        id: "001303",
        name: "NOD.Cg-Prkdc scid/J",
        description: "NOD SCID - Severe combined immunodeficiency",
        jaxUrl: "https://www.jax.org/strain/001303"
      },
      {
        id: "001803",
        name: "CBySmn.Cg-Prkdc scid/J",
        description: "CB17 SCID - SCID mutation on BALB/c background",
        jaxUrl: "https://www.jax.org/strain/001803"
      },
      {
        id: "002216",
        name: "B6.129S7-Rag1 tm1Mom/J",
        description: "B6 RAG1 KO - T and B cell deficient",
        jaxUrl: "https://www.jax.org/strain/002216"
      },
      {
        id: "007850",
        name: "J:NU",
        description: "Nude mice with T cell deficiency",
        jaxUrl: "https://www.jax.org/strain/007850"
      },
      {
        id: "002019",
        name: "NU/J",
        description: "Athymic nude mice",
        jaxUrl: "https://www.jax.org/strain/002019"
      }
    ],
    "Alzheimer": [
      {
        id: "034830",
        name: "3xTg-AD",
        fullName: "B6;129-Tg(APPSwe,tauP301L)1Lfa Psen1tm1Mpm/Mmjax",
        description: "Triple transgenic Alzheimer's disease model",
        jaxUrl: "https://www.mmrrc.org/catalog/sds.php?mmrrc_id=34830"
      },
      {
        id: "034829",
        name: "APP/PS1",
        fullName: "B6C3-Tg(APPswe,PSEN1dE9)85Dbo/Mmjax",
        description: "Double transgenic Alzheimer's model",
        jaxUrl: "https://www.mmrrc.org/catalog/sds.php?mmrrc_id=34829"
      },
      {
        id: "034848",
        name: "5XFAD (B6.Cg)",
        fullName: "B6.Cg-Tg(APPSwFlLon,PSEN1*M146L*L286V)6799Vas/Mmjax",
        description: "Rapid onset familial Alzheimer's disease model",
        jaxUrl: "https://www.mmrrc.org/catalog/sds.php?mmrrc_id=34848"
      },
      {
        id: "034840",
        name: "5XFAD (B6SJL)",
        fullName: "B6SJL-Tg(APPSwFlLon,PSEN1*M146L*L286V)6799Vas/Mmjax",
        description: "Alternative background for familial AD research",
        jaxUrl: "https://www.mmrrc.org/catalog/sds.php?mmrrc_id=34840"
      }
    ],
    "Mutant": [
      {
        id: "034860",
        name: "B6.Cg-Tg(K18-ACE2)2Prlmn/J",
        description: "ACE2 transgenic model for COVID-19 research",
        jaxUrl: "https://www.jax.org/strain/034860"
      },
      {
        id: "002468",
        name: "KK.Cg-Ay/J",
        description: "Yellow obese diabetes model",
        jaxUrl: "https://www.jax.org/strain/002468"
      },
      {
        id: "002020",
        name: "C57BL/6J-Apc min/J",
        description: "Adenomatous polyposis coli tumor model",
        jaxUrl: "https://www.jax.org/strain/002020"
      },
      {
        id: "002052",
        name: "B6.129P2-Apoe tm1Unc/J",
        description: "Apolipoprotein E knockout atherosclerosis",
        jaxUrl: "https://www.jax.org/strain/002052"
      },
      {
        id: "001011",
        name: "CBA/CaHN-Btk xid/J",
        description: "X-linked immunodeficiency",
        jaxUrl: "https://www.jax.org/strain/001011"
      },
      {
        id: "001801",
        name: "C57BL/10ScSn-Dmd mdx/J",
        description: "Duchenne muscular dystrophy model",
        jaxUrl: "https://www.jax.org/strain/001801"
      },
      {
        id: "000457",
        name: "B10.RIII-H2 r H2-T18 b/(71NS)SnJ",
        description: "MHC congenic strain",
        jaxUrl: "https://www.jax.org/strain/000457"
      },
      {
        id: "000632",
        name: "B6.Cg-Lep ob/J",
        description: "Leptin deficient obese model",
        jaxUrl: "https://www.jax.org/strain/000632"
      },
      {
        id: "000697",
        name: "B6.BKS(D)-Lepr db/J",
        description: "Leptin receptor deficient diabetes",
        jaxUrl: "https://www.jax.org/strain/000697"
      },
      {
        id: "000642",
        name: "BKS.Cg-Dock7 m +/+ Leprdb/J",
        description: "Diabetes model with misty background",
        jaxUrl: "https://www.jax.org/strain/000642"
      },
      {
        id: "002207",
        name: "B6.129S7-Ldlr tm1her/J",
        description: "LDL receptor knockout atherosclerosis",
        jaxUrl: "https://www.jax.org/strain/002207"
      },
      {
        id: "002014",
        name: "B6.SJL-Ptprc a Pepc b/BoyJ",
        description: "CD45.1 congenic for cell tracking",
        jaxUrl: "https://www.jax.org/strain/002014"
      },
      {
        id: "003303",
        name: "C.Cg-Tg(DO11.10)10Dlo/J",
        description: "Ovalbumin-specific TCR transgenic",
        jaxUrl: "https://www.jax.org/strain/003303"
      },
      {
        id: "006494",
        name: "B6CBA-Tg(HDexon1)62Gpb/3J",
        description: "Huntington's disease model",
        jaxUrl: "https://www.jax.org/strain/006494"
      },
      {
        id: "002726",
        name: "B6SJL-Tg(SOD1*G93A)1Gur/J",
        description: "ALS (Lou Gehrig's disease) model",
        jaxUrl: "https://www.jax.org/strain/002726"
      },
      {
        id: "004194",
        name: "B6.Cg-Tg(TcraTcrb)425Cbn/J",
        description: "OT-I TCR transgenic T cells",
        jaxUrl: "https://www.jax.org/strain/004194"
      },
      {
        id: "000406",
        name: "B6.PL-Thy1 a/CyJ",
        description: "Thy-1.1 congenic strain",
        jaxUrl: "https://www.jax.org/strain/000406"
      },
      {
        id: "000058",
        name: "B6(Cg)-Tyrc-2J/J",
        description: "Albino C57BL/6J strain",
        jaxUrl: "https://www.jax.org/strain/000058"
      }
    ]
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // URL에서 카테고리 파라미터 읽기
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const category = params.get('category');
    if (category && Object.keys(strainsByCategory).includes(category)) {
      setActiveTab(category);
    }
  }, [location.search]);

  // 탭 변경 시 URL 업데이트
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    navigate(`/animal-list?category=${encodeURIComponent(value)}`, { replace: true });
  };

  return (
    <section className="py-20 bg-gradient-section">
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            JAX<sup>®</sup> transgenic 모델
          </h1>
          <p className="text-sm sm:text-lg text-muted-foreground max-w-3xl mx-auto mb-6 break-keep leading-relaxed">
            Central TG Inc.는 The Jackson Laboratory의 <br />
            고품질, 검증된 transgenic 모델을 대한민국 연구기관에 공급합니다. <br />
            연구 목적에 맞는 모델을 선택하여 문의해 주세요.
          </p>
          <a
            href="https://mice.jax.org/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button className="bg-jax-blue hover:bg-jax-blue/90 text-white font-bold px-8 py-3">
              <ExternalLink className="w-4 h-4 mr-2" />
              JAX<sup>®</sup> 공식 사이트에서 전체 계통 검색
            </Button>
          </a>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-3xl mx-auto mt-6">
            <p className="text-sm font-bold text-amber-800 leading-relaxed">
              ⚠️ JAX 모델은 연구 목적에 한해 제공되며,<br />
              사용은 JAX의 <a 
                href="https://www.jax.org/about-us/legal-information/terms-and-conditions-of-product-use" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-jax-blue hover:text-jax-blue/80 underline font-bold"
              >
                Terms and Conditions of Product Use
              </a> 에 따라 제한됩니다.
            </p>
          </div>
        </div>

        {/* 탭 메뉴 */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="mb-8">
          {/* Mobile Dropdown */}
          <div className="block sm:hidden mb-6">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  className="w-full max-w-xs mx-auto flex justify-between items-center bg-background border-border hover:bg-muted/50"
                >
                  <span className="font-medium">{activeTab}</span>
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                className="w-56 bg-background border-border shadow-lg z-50" 
                align="center"
              >
                {Object.keys(strainsByCategory).map((category) => (
                  <DropdownMenuItem 
                    key={category}
                    onClick={() => handleTabChange(category)}
                    className="cursor-pointer hover:bg-muted focus:bg-muted"
                  >
                    {category}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Desktop Tabs */}
          <TabsList className="hidden sm:grid w-full grid-cols-6 max-w-5xl mx-auto">
            <TabsTrigger value="Inbred" className="text-center">Inbred</TabsTrigger>
            <TabsTrigger value="Outbred" className="text-center">Outbred</TabsTrigger>
            <TabsTrigger value="Hybrid" className="text-center">Hybrid</TabsTrigger>
            <TabsTrigger value="Immunodeficient" className="text-center">Immunodeficient</TabsTrigger>
            <TabsTrigger value="Alzheimer" className="text-center">Alzheimer</TabsTrigger>
            <TabsTrigger value="Mutant" className="text-center">Mutant</TabsTrigger>
          </TabsList>


          {/* 카테고리별 계통 리스트 */}
          {Object.entries(strainsByCategory).map(([category, strains]) => (
            <TabsContent key={category} value={category} className="mt-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {strains.map((strain) => (
                  <Card key={strain.id} className="shadow-clean hover:shadow-card transition-all duration-300 border-0">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h2 className="text-xl font-bold text-foreground mb-1">
                            {strain.name}
                          </h2>
                          {strain.fullName && (
                            <p className="text-xs text-muted-foreground mb-1 leading-tight">
                              {strain.fullName}
                            </p>
                          )}
                          <p className="text-sm text-muted-foreground">
                            {strain.id}
                          </p>
                        </div>
                      </div>
                      
                      <p className="text-muted-foreground leading-relaxed mb-6">
                        {strain.description}
                      </p>
                      
                      <a
                        href={strain.jaxUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <Button className="w-full bg-jax-blue hover:bg-jax-blue/90 text-white font-medium">
                          Request Quote (In Details)
                          <ExternalLink className="w-4 h-4 ml-2" />
                        </Button>
                      </a>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          ))}

        </Tabs>

      </div>
    </section>
  );
};

export default AnimalList;
